/* eslint-disable import/no-default-export */
import classNames from 'classnames';
// eslint-disable-next-line no-restricted-imports
import { LayerManagerPortal, Layers } from 'components/layer-manager';
// eslint-disable-next-line no-restricted-imports
import { PopoverOwnerContext } from 'components/popover/context';
// eslint-disable-next-line no-restricted-imports
import Keybingings, { Key } from 'components/shared/keybindings';
// eslint-disable-next-line no-restricted-imports
import { PopoverName } from 'modules';
import React from 'react';
import { debounce } from 'underscore';

import { TestId } from '@trello/webdriverio-tests';
// eslint-disable-next-line no-restricted-imports
import styles from 'components/popover/popover.less';
// eslint-disable-next-line no-restricted-imports
import { Rect } from 'components/rect';

// This allows the fancy scrollbar to show in its entirety
const POPOVER_PADDING = 17;
const DEFAULT_POPOVER_WIDTH = 304;

// Magic number alert! 47 is the height of the global header and a little
// extra padding for the bottom. we don't want the popover to overflow past
// the bottom of the screen.
const HEADER_HEIGHT_OFFSET = 42;

interface OwnProps {
  // Normal behavior for popover is to receive focus.
  // Components like the Search bar do not behave like that.
  disableAutoFocus?: boolean;
  hidden?: boolean;
  contentClassName?: string;
  children: (
    options: {
      maxHeight: number;
      closePopover: () => void;
    },
  ) => React.ReactNode;
  className?: string;
  left?: number;
  name: PopoverName;
  onClose: (trackingMethod: string) => void;
  testId?: TestId;
  top?: number;
  width?: number;
}

interface ContextProps {
  addDescendant: (descendant: HTMLElement) => void;
  descendants: HTMLElement[];
  removeDescendant: (popoverElem: HTMLElement) => void;
}

export interface PopoverProps extends OwnProps, ContextProps {}

interface PopoverState {
  left: number;
  maxHeight: number;
  top: number;
  width: number;
}

export class PopoverWithoutContext extends React.Component<
  PopoverProps,
  PopoverState
> {
  popoverElem: HTMLElement | null;
  contentRef = React.createRef<HTMLDivElement>();

  hasClosed = false;
  updateAfterNextRender = false;
  isComponentMounted = false;

  state: PopoverState = {
    left: 0,
    maxHeight: 0,
    top: 0,
    width: 0,
  };

  componentDidMount() {
    this.isComponentMounted = true;
    const popoverElem = this.popoverElem;

    if (!this.props.hidden) {
      this.updateSizeAndPosition();
      this.registerWindowHandlers();
      if (popoverElem && !this.props.disableAutoFocus) {
        // move keyboard focus to the newly rendered popover
        popoverElem.focus();
      }
    }
  }

  componentDidUpdate(prevProps: PopoverProps) {
    if (!prevProps.hidden && this.props.hidden) {
      this.unregisterWindowHandlers();
    } else if (prevProps.hidden && !this.props.hidden) {
      this.registerWindowHandlers();
      this.debounceUpdateSizeAndPosition();
      if (this.updateAfterNextRender) {
        // The popover needs to be rendered once to correctly calculate the
        // popover's height.
        this.updateAfterNextRender = false;
        this.updateSizeAndPositionOnce();
      }
    }
  }

  componentWillUnmount() {
    this.isComponentMounted = false;

    // We do this to make sure that if a nested popover gets unmounted, we also
    // call `onClose` so that it doesn't automatically open if the parent popover
    // is opened later (since it will still be set as open in Redux)
    if (!this.hasClosed) {
      this.onClose();
    }

    if (this.popoverElem) {
      this.props.removeDescendant(this.popoverElem);
    }

    this.unregisterWindowHandlers();
  }

  // Event bubbling plays a key role in allowing us to support toggling
  // the display of a popover when clicking the popover's trigger element.
  private registerWindowHandlers() {
    // Because this listener is attached to the document, that means that
    // clicking the trigger for a popover while the popover is visible
    // causes the trigger's click handler to fire (showing the popover),
    // and then the document click handler fires, hiding the popover.
    document.addEventListener('click', this.handleOutsideClick);
    window.addEventListener('resize', this.debounceUpdateSizeAndPosition);
  }

  private unregisterWindowHandlers() {
    document.removeEventListener('click', this.handleOutsideClick);
    window.removeEventListener('resize', this.debounceUpdateSizeAndPosition);
  }

  updateSizeAndPosition = () => {
    if (!this.isComponentMounted) {
      return false;
    }

    const changed = this.updateSizeAndPositionOnce();
    // The popover needs to be rendered once to correctly calculate the
    // popover's height
    if (changed) {
      this.updateAfterNextRender = true;
    }
  };

  // This sizing and positioning logic is lifted directly from the Classic
  // implementation of popovers. It has been adapted to use native DOM
  // methods and to only update dimensions after all measuring is complete.
  // Returns whether or not the size and position changed.
  updateSizeAndPositionOnce = (): boolean => {
    let width = DEFAULT_POPOVER_WIDTH;
    let top = this.props.top !== undefined ? this.props.top : -9999;
    let left = this.props.left !== undefined ? this.props.left : -9999;

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    const widthProp = this.props.width;
    if (widthProp) {
      if (widthProp > windowWidth) {
        width = windowWidth - 16;
      } else {
        width = widthProp;
      }
    }

    const maxHeight = windowHeight - (HEADER_HEIGHT_OFFSET + 5);

    // Ensure that the top of the element is visible
    top = top >= 0 ? top : 5;

    left = left >= 0 ? left : 5;
    const right = left + width + 16;

    // We want to ensure the right corner is on the screen
    if (right > windowWidth) {
      // Move the element back onto the screen
      left = windowWidth - (width + 5);
    }

    // Only update state if it's changed. This is necessary to avoid infinite
    // render loops from updating state inside of `componentDidUpdate`.
    if (
      left === this.state.left &&
      top === this.state.top &&
      maxHeight === this.state.maxHeight &&
      width === this.state.width
    ) {
      return false;
    }

    this.setState({ left, top, maxHeight, width });

    return true;
  };

  getIdealTopPosition(
    targetRectTop: number,
    offsetTop: number,
    windowHeight: number,
    popoverHeight: number,
  ) {
    const beneathTarget = targetRectTop + offsetTop;
    const aboveTarget = targetRectTop - popoverHeight - 6;
    const aboveBottomOfWindow = windowHeight - popoverHeight - 6;

    const doesNotOverflowWindow = (topPos: number) =>
      topPos >= HEADER_HEIGHT_OFFSET && topPos + popoverHeight <= windowHeight;

    const idealTopPosition =
      [beneathTarget, aboveTarget, aboveBottomOfWindow].find(
        doesNotOverflowWindow,
      ) || HEADER_HEIGHT_OFFSET;

    return idealTopPosition;
  }

  handleCloseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    this.onClose();
  };

  handleOutsideClick = (e: MouseEvent) => {
    // const targetNode = this.props.target && this.props.target();

    switch (true) {
      case e.defaultPrevented:
      case !this.popoverElem:
      case this.props.descendants.some((elem) =>
        elem.contains(e.target as Node),
      ):
      case !!this.popoverElem && this.popoverElem.contains(e.target as Node):
        /**
         * If the click was defaultPrevented, there is no popover element,
         * or if the click target is within the popover itself, do nothing.
         */
        break;
      // case !!targetNode && targetNode === (e.target as Node):
      // case !!targetNode && targetNode.contains(e.target as Node):
      //   /**
      //    * If the click target is a descendent of the popover target or
      //    * is the target itself, pop a layer off the popover.
      //    */
      //   this.pop();
      //   break;
      default:
        /**
         * If the click target is outside the popover and the popover target,
         * close the popover normally.
         */
        this.onClose('by clicking outside popover');
    }
  };

  handleEscapePress = (e: React.KeyboardEvent<HTMLElement>) => {
    if (this.props.descendants.length === 0) {
      this.onClose('by pressing escape key');
    }
  };

  debounceUpdateSizeAndPosition = debounce(this.updateSizeAndPosition, 300);

  onClose = (trackingMethod = '') => {
    if (!this.props.hidden) {
      this.hasClosed = true;
      this.props.onClose(trackingMethod);
    }
  };

  scrollContentToTop = () => {
    if (this.contentRef.current) {
      this.contentRef.current.scrollTop = 0;
    }
  };

  keybindings = {
    [Key.Escape]: {
      callback: this.handleEscapePress,
      preventDefault: true,
      stopPropagation: true,
      isGlobal: true,
    },
  };

  render() {
    const {
      addDescendant,
      className = '',
      contentClassName,
      hidden,
      testId,
    } = this.props;
    const { left, maxHeight, top, width } = this.state;

    return (
      <LayerManagerPortal layer={Layers.Popover}>
        <Keybingings keybindings={this.keybindings}>
          <div
            className={classNames(
              styles.popover,
              className || styles.defaultPopover,
            )}
            data-test-id={testId}
            ref={(elem) => {
              this.popoverElem = elem;

              // Element being mounted into the DOM
              if (elem !== null) {
                addDescendant(elem);
              }
            }}
            style={{ left, top, width, maxHeight }}
            hidden={hidden}
            tabIndex={-1}
            role="dialog"
          >
            <Rect>
              {({ rect }) => (
                <>
                  <div
                    className={classNames(
                      styles.popoverContent,
                      contentClassName || styles.defaultPopoverContent,
                    )}
                    ref={this.contentRef}
                    style={{
                      maxHeight:
                        maxHeight - POPOVER_PADDING - (rect ? rect.height : 0),
                    }}
                  >
                    {this.props.children({
                      maxHeight,
                      closePopover: this.onClose,
                    })}
                  </div>
                </>
              )}
            </Rect>
          </div>
        </Keybingings>
      </LayerManagerPortal>
    );
  }
}

interface PopoverDescendantState {
  descendants: HTMLElement[];
}

class PopoverContainer extends React.Component<
  OwnProps,
  PopoverDescendantState
> {
  state: PopoverDescendantState = {
    descendants: [],
  };

  render() {
    return (
      <PopoverOwnerContext.Consumer>
        {({ addDescendant, removeDescendant }) => {
          return (
            <PopoverOwnerContext.Provider
              value={{
                // We wrap the context methods each time we render a
                // nested popover, calling the parent's method along
                // with the instance method. This means that each
                // descendant will be added and removed from all of
                // its ancestor popovers automatically
                addDescendant: (descendant: HTMLElement) => {
                  this.addDescendant(descendant);
                  addDescendant(descendant);
                },
                removeDescendant: (descendant: HTMLElement) => {
                  this.removeDescendant(descendant);
                  removeDescendant(descendant);
                },
              }}
            >
              <PopoverWithoutContext
                {...this.props}
                addDescendant={addDescendant}
                descendants={this.state.descendants}
                removeDescendant={removeDescendant}
              />
            </PopoverOwnerContext.Provider>
          );
        }}
      </PopoverOwnerContext.Consumer>
    );
  }

  addDescendant = (elem: HTMLElement) => {
    if (this.state.descendants.includes(elem)) {
      return;
    }

    this.setState({
      descendants: [...this.state.descendants, elem],
    });
  };

  removeDescendant = (descendant: HTMLElement) => {
    this.setState({
      descendants: this.state.descendants.filter((d) => d !== descendant),
    });
  };
}

export default PopoverContainer;



// WEBPACK FOOTER //
// ./app/gamma/src/components/popovers/boards-menu/boards-menu-popover.tsx